import React, { createContext, useContext, useState, useEffect } from 'react';
import { addToCart as addToCartGuest, removeItemFromCart, getCart as getCartGuest, clearCart, clearCartAfterCheckoutRequest, clearCartOnLogin } from '../api';
import { addToCartAuth, getCartAuth, updateCartAuth, deleteCartAuth } from '../api';
import { AuthContext } from '../../auth/context/AuthContext';
const CartContext = createContext();

export const CartProvider = ({ children }) => {
    const { isAuthenticated,user} = useContext(AuthContext);
    const [cart, setCart] = useState([]);
    const [cartCount, setCartCount] = useState(0);
    const [isCartLoaded, setIsCartLoaded] = useState(false);
    const customerId = user?.id;

    const getGuestCartCount = (cartItems = []) => cartItems.length;
  
    useEffect(() => {
      const loadCart = async () => {
        if (isAuthenticated) {
          try {
            const authCart = await getCartAuth();
            setCart(authCart);
            setCartCount(authCart.length);
            setIsCartLoaded(true);  // Flag updated here for authenticated cart
          } catch (error) {
            console.error('Error fetching authenticated cart:', error);
          }
        } else {
          const guestCart = getCartGuest() || [];
          setCart(guestCart);
          setCartCount(getGuestCartCount(guestCart));
        }
      };
  
      loadCart();
  
      // For guest users, listen to storage changes
      if (!isAuthenticated) {
        const handleStorageChange = () => {
          const updatedCart = getCartGuest();
          setCart(updatedCart);
          setCartCount(getGuestCartCount(updatedCart));
        };
  
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
      }
    }, [isAuthenticated]);
  
   // Add to cart function – uses the appropriate endpoint
  const addToCart = async (product) => {
    if (isAuthenticated) {
      try {
        // Assumes product contains { id, quantity }
        await addToCartAuth(customerId, product.id, product.quantity);
        const updatedCart = await getCartAuth();
        setCart(updatedCart);
        setCartCount(updatedCart.length);
      } catch (error) {
        console.error('Error adding product to authenticated cart:', error);
      }
    } else {
      try {
        await addToCartGuest(product);
        const updatedCart = getCartGuest();
        setCart(updatedCart);
        setCartCount(getGuestCartCount(updatedCart));
        //toast.success('Product added to cart');
      } catch (error) {
        console.error('Error adding product to guest cart:', error);
      }
    }
  };

    
  
    // Function to remove an item and update context
    const removeFromCart = async (id) => {
      if (isAuthenticated) {
        try {
          // If your API supports removing a single item,
          // you might have a dedicated endpoint.
          // As an alternative, setting quantity to 0 might remove the item.
          await updateCartAuth(id, 0);
          const updatedCart = await getCartAuth();
          setCart(updatedCart);
          setCartCount(updatedCart.length);
        } catch (error) {
          console.error('Error removing product from authenticated cart:', error);
        }
      } else {
        removeItemFromCart(id);
        const updatedCart = getCartGuest();
        setCart(updatedCart);
        setCartCount(getGuestCartCount(updatedCart));
      }
    };
  

    const updateQuantity = async (id, newQuantity) => {
      if (isAuthenticated) {
        try {
          await updateCartAuth(id, newQuantity);
          const updatedCart = await getCartAuth();
          setCart(updatedCart);
          setCartCount(updatedCart.length);
        } catch (error) {
          console.error('Error updating quantity for authenticated cart:', error);
        }
      } else {
        // For guests, update localStorage
        const updatedQuantity = Math.max(parseInt(newQuantity, 10) || 1, 1);
        const updatedCart = cart.map(item =>
          item.id === id ? { ...item, quantity: updatedQuantity } : item
        );
        if (JSON.stringify(cart) !== JSON.stringify(updatedCart)) {
          setCart(updatedCart);
          localStorage.setItem('cart', JSON.stringify(updatedCart));
          setCartCount(getGuestCartCount(updatedCart));
        }
      }
    };

    // Clear the entire cart
  const clearCartItems = () => {
    if (isAuthenticated) {
      deleteCartAuth()
        .then(() => {
          setCart([]);
          setCartCount(0);
        })
        .catch(error => console.error('Error clearing authenticated cart:', error));
    } else {
      clearCart();
      setCart([]);
      setCartCount(0);
    }
  };

    // Clear guest cart upon login if needed
  const clearCartItemsLogin = () => {
    if (!isAuthenticated) {
      clearCartOnLogin();
      setCart([]);
      setCartCount(0);
    }
  };
     
    const clearCartAfterCheckout = () => {
      if (isAuthenticated) {
        deleteCartAuth()
          .then(() => {
            setCart([]);
            setCartCount(0);
          })
          .catch(error => console.error('Error clearing authenticated cart:', error));
      } else {
        clearCartAfterCheckoutRequest();
        setCart([]);
        setCartCount(0);
      }
    };

    
  
    return (
      <CartContext.Provider
        value={{
          cart,
          cartCount,
          isCartLoaded,
          addToCart,
          removeFromCart,
          updateQuantity,
          clearCartItems,
          clearCartItemsLogin,
          clearCartAfterCheckout
        }}
      >
        {children}
      </CartContext.Provider>
    );
  };
  
  
  // Custom hook for easier access to context
  export const useCart = () => useContext(CartContext);